// Everyone on record. ?connected=1 is the Connections view, which replaces connections-ui: a status and note per founder.
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../../../lib'
import { Busy, Button, Err, Page, Strip, Table, useCopy, useCursor, useKit, type Column } from '../kit'
import { STATUS_WORD, TRACK_LABEL, fillMessage, linkedinHref, nameOf, queryOf, rememberList, safeHref, statusOptions, statusWord, useFilters, useMeta, usePaged, useWrite, type Connection, type Imported, type Sync, type Track } from './lib'
import { useMarkMessaged } from './actions'
import { Check, FilterBar, LoadMore, ReadLine, SearchField, Select, lastTouch } from './parts'

type Row = {
  id: string; name: string; title: string | null; email: string | null; linkedin_url: string | null; company_id: string | null; company: string | null; do_not_contact: boolean | null
  connections: Connection[]; tracks: { track: Track; stage: string; stage_label: string }[]; last_at: string | null; last_actor: string | null; last_summary: string | null
  page_url: string | null; send_id: string | null; send_message: string | null; send_link: string | null; send_state: string | null; dm: string; old_wording: boolean
  connector?: { rank: number; doors: number | null; targets: string | null; routes: string | null; ask: string | null; when: string | null; headline: string | null; old_wording: boolean }
}
type Data = { at: string; rows: Row[]; total: number; truncated: boolean; next: string | null; sync: Sync; imported: Imported }

function NoteField({ value, save, testid }: { value: string; save: (v: string) => void; testid?: string }) {
  const [text, setText] = useState(value)
  return <input className="st-input st-note-field" aria-label="Note" placeholder="Note" value={text} data-testid={testid} onChange={(e) => setText(e.target.value)} onBlur={() => { if (text !== value) save(text) }} />
}

const NO_FOUNDER = 'A status belongs to a founder. Pick one in Founder to mark it'

export function PeoplePage() {
  const { sp, get, put } = useFilters()
  const navigate = useNavigate()
  const meta = useMeta()
  const write = useWrite()
  const markMessaged = useMarkMessaged()
  const { toast } = useKit()
  const connected = get('connected') === '1'
  const view = get('view')
  const search = queryOf(sp, ['q', 'connected', 'view', 'status', 'founder', 'kind', 'page', 'warm', 'internal'])
  const q = usePaged<Data, Row>(['crm', 'people', search], `/api/staff/crm/people${search}`, 60_000)
  const { rows, head } = q
  const open = (r: Row) => navigate(`/staff/people/${r.id}`)
  const { cur, setCur } = useCursor(rows, (r) => r.id, open)
  useEffect(() => { if (rows.length) rememberList('person', rows.map((r) => r.id)) }, [rows])
  const me = get('founder') || meta.data?.me || null

  // Whose mark this page edits: the chosen founder, else the signed-in one, else the first who knows them.
  const whose = (r: Row): string | null => me ?? r.connections[0]?.founder ?? null
  const mine = (r: Row) => r.connections.find((k) => k.founder === whose(r))
  const others = (r: Row) => r.connections.filter((k) => k.founder !== whose(r))
  const setConn = (r: Row, patch: Record<string, string | null>) => {
    const founder = whose(r)
    if (founder) void write('POST', `/api/staff/crm/people/${r.id}/connection`, { founder, ...patch }, 'Saved')
  }
  const copy = useCopy()
  /** Copies only. Mark messaged is its own press, because a copied DM isn't a sent one. */
  const dm = async (r: Row) => {
    await copy(r.send_message ? fillMessage(r.send_message, { link: r.send_link ?? r.page_url, person: r.name, company: r.company }) : r.dm, 'DM copied')
  }
  const messaged = (r: Row) => {
    const founder = whose(r)
    if (founder) void markMessaged(r.id, founder)
  }

  const base: Column<Row>[] = [
    { key: 'name', head: 'Name', primary: true, cell: (r) => <Link className="st-strong" to={`/staff/people/${r.id}`}>{r.name}</Link> },
    { key: 'title', head: 'Title', cell: (r) => r.title },
    { key: 'company', head: 'Company', cell: (r) => (r.company_id ? <><Link className="st-link" to={`/staff/companies/${r.company_id}`}>{r.company}</Link>{r.do_not_contact ? <span className="st-bad"> Do not contact</span> : null}</> : null) },
  ]
  const plain: Column<Row>[] = [...base,
    { key: 'email', head: 'Email', cell: (r) => r.email },
    { key: 'conn', head: 'Connected to', cell: (r) => r.connections.map((k) => nameOf(k.founder)).join(', ') },
    { key: 'last', head: 'Last touch', nowrap: true, cell: lastTouch },
  ]
  const conn: Column<Row>[] = [...base,
    { key: 'kind', head: 'Kind', cell: (r) => r.tracks.map((t) => TRACK_LABEL[t.track]).join(', ') },
    { key: 'status', head: nameOf(me) ? `${nameOf(me)}'s status` : 'Status', label: 'Status', nowrap: true, cell: (r) => (
      <span className="st-stack st-tight">
        <select className="st-select" aria-label={`Status for ${r.name}`} value={mine(r)?.status ?? ''} data-testid="st-conn-status" disabled={!whose(r)} title={whose(r) ? undefined : NO_FOUNDER} onChange={(e) => setConn(r, { status: e.target.value || null })}>
          {statusOptions(mine(r)?.status).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {others(r).map((k) => <span key={k.founder} className="st-muted">{nameOf(k.founder)}: {statusWord(k.status) || 'connected'}</span>)}
      </span>
    ) },
    { key: 'note', head: 'Note', cell: (r) => <NoteField key={`${r.id}:${whose(r)}:${mine(r)?.note ?? ''}`} value={mine(r)?.note ?? ''} save={(v) => setConn(r, { note: v || null })} testid="st-conn-note" /> },
    { key: 'acts', head: '', label: 'Do', nowrap: true, cell: (r) => (
      <span className="st-conn-acts">
        {safeHref(r.page_url) ? <Button tone="quiet" data-testid="st-conn-copy" onClick={() => void copy(r.page_url!, 'Link copied')}>Copy link</Button> : <span className="st-muted">No page</span>}
        <Button tone="quiet" data-testid="st-conn-dm" onClick={() => void dm(r)}>Copy DM</Button>
        {r.old_wording ? <span className="st-warn" data-testid="st-old-wording">Old wording</span> : null}
        <Button tone="quiet" data-testid="st-conn-messaged" onClick={() => messaged(r)}>Mark messaged</Button>
        <a className="st-btn quiet" href={linkedinHref(r.linkedin_url, r.name, r.company)} target="_blank" rel="noreferrer">LinkedIn</a>
      </span>
    ) },
    { key: 'last', head: 'Last touch', nowrap: true, cell: lastTouch },
  ]

  const doors: Column<Row>[] = [
    { key: 'rank', head: '#', num: true, cell: (r) => r.connector?.rank },
    { key: 'name', head: 'Name', primary: true, cell: (r) => <Link className="st-strong" to={`/staff/people/${r.id}`}>{r.name}</Link> },
    { key: 'headline', head: 'Headline', cell: (r) => r.connector?.headline },
    { key: 'doors', head: 'Doors', num: true, cell: (r) => r.connector?.doors },
    { key: 'routes', head: 'Routes', cell: (r) => r.connector?.routes },
    { key: 'targets', head: 'Opens', cell: (r) => r.connector?.targets },
    { key: 'when', head: 'When', cell: (r) => r.connector?.when },
    { key: 'ask', head: 'Draft ask', cell: (r) => (r.connector?.ask ? (
      <span className="st-stack st-tight">
        <span>{r.connector.ask}</span>
        {r.connector.old_wording ? <span className="st-warn" data-testid="st-old-wording">Old wording</span> : null}
        <span><Button tone="quiet" data-testid="st-ask-copy" onClick={() => void copy(r.connector!.ask!, 'Ask copied')}>Copy ask</Button></span>
      </span>
    ) : null) },
  ]
  const sorted = view === 'connectors' ? [...rows].sort((a, b) => (a.connector?.rank ?? 0) - (b.connector?.rank ?? 0)) : rows

  return (
    <Page title={view === 'connectors' ? 'Connectors' : connected ? 'Connections' : 'People'} testid="st-people" sub={<ReadLine at={head?.at} sync={head?.sync} what="Records" imported={head?.imported} />}>
      <Strip
        testid="st-people-tab"
        value={view === 'connectors' ? 'connectors' : connected ? 'connections' : 'all'}
        onChange={(k) => put({ connected: k === 'connections' ? '1' : null, view: k === 'connectors' ? k : null, status: null, founder: null, kind: null, page: null, warm: null })}
        items={[{ key: 'all', label: 'Everyone' }, { key: 'connections', label: 'Connections' }, { key: 'connectors', label: 'Connectors' }]}
      />
      {connected && !me ? <p className="st-note" data-testid="st-conn-why">{NO_FOUNDER}</p> : null}
      <FilterBar
        on={['status', 'founder', 'kind', 'page', 'warm', 'internal'].filter((k) => get(k)).length}
        more={<Check label="Show internal" value={get('internal') === '1'} testid="st-internal" onChange={(v) => put({ internal: v ? '1' : null })} />}
        search={<SearchField value={get('q')} onChange={(v) => put({ q: v })} placeholder="Search name, email or company" testid="st-q" />}
      >
        {connected ? (
          <>
            <Select label="Founder" value={get('founder')} testid="st-founder" onChange={(v) => put({ founder: v })} options={[{ value: '', label: 'Any founder' }, ...(meta.data?.owners ?? []).map((o) => ({ value: o, label: nameOf(o) }))]} />
            <Select label="Kind" value={get('kind')} testid="st-kind" onChange={(v) => put({ kind: v })} options={[{ value: '', label: 'All kinds' }, { value: 'startup', label: 'Startup' }, { value: 'partner', label: 'Partner' }, { value: 'f26', label: 'F26' }, { value: 'none', label: 'No track' }]} />
            <Select label="Page" value={get('page')} testid="st-page" onChange={(v) => put({ page: v })} options={[{ value: '', label: 'Any page' }, { value: 'live', label: 'Has a page' }, { value: 'none', label: 'No page yet' }]} />
            <Select label="Status" value={get('status')} testid="st-status" onChange={(v) => put({ status: v })} options={[{ value: '', label: 'Any status' }, { value: 'todo', label: 'Not contacted' }, ...Object.entries(STATUS_WORD).map(([value, label]) => ({ value, label }))]} />
            <Check label="Founder or exec only" value={get('warm') === '1'} testid="st-warm" onChange={(v) => put({ warm: v ? '1' : null })} />
          </>
        ) : null}
        {head ? <span className="st-muted">{head.total.toLocaleString('en-US')} people</span> : null}
      </FilterBar>
      {q.error ? <Err error={q.error} /> : !head ? <Busy /> : (
        <>
          <Table rows={sorted} columns={view === 'connectors' ? doors : connected ? conn : plain} rowKey={(r) => r.id} cur={cur} onPick={(r) => setCur(r.id)} onOpen={connected ? undefined : open} testid="st-people-table" empty={connected ? 'No connection matches.' : 'Nobody matches.'} />
          <LoadMore shown={rows.length} total={head.total} more={q.more} loading={q.loading} load={q.load} />
        </>
      )}
    </Page>
  )
}
